import { forwardRef, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from '../i18n';
import { COLORS } from '../lib/theme';
import type { GeoLine, GeoPt, MapObject, PinFolder } from '../lib/types';
import {
  buildPinFolderView,
  lineDisplayName,
  pinDisplayName,
  type LineKindLabels,
  type PinFolderCollection,
  type PinSortOrder,
  type PinTypeLabeler,
} from '../lib/pinFolderView';
import { Icon, type IconName } from './Icon';
import { PinGlyph } from './MapPin';
import { usePinTypeCatalog } from './PinTypeCatalogProvider';
import { WaSelect } from './WaSelect';
import {
  DeleteFolderDialog,
  DeletePinsDialog,
  FolderNameDialog,
  MovePinsDialog,
  PinMenu,
  type MenuItem,
} from './PinFolderDialogs';

/**
 * CLUB-676 — the team's map pins, grouped by folder.
 *
 * Web has never had a pin list: pins existed only as sprites on the map, and a
 * ground with 200 of them was one flat pile of artwork. The app has grouped the
 * same pins into folders for years, so a hunter who filed their pins on the
 * phone found none of that structure in the browser (CLUB-675).
 *
 * Built to the Figma section "01 · Team ▸ Map tab", states A–G and J: default,
 * folder expanded, searching, selection, drag into a folder, empty, loading and
 * dark mode. The grouping rules are NOT decided here — they are ported from the
 * app in `lib/pinFolderView`, so both platforms order and split the same data
 * the same way.
 *
 * ── Two collections, never one total ──────────────────────────────────────
 * A team map draws the ground's pins AND the signed-in hunter's own, and the
 * two are labelled and counted separately here. The hunter's collection is
 * keyed on their uid and is not ground-filtered, so a pin in it can be
 * hundreds of kilometres from the ground on screen; counting the two together
 * is what had this panel claiming "15 pins on this ground" above a chip
 * reading 3, with the panel's own ungrouped row saying 17. The headings appear
 * only when the viewer has pins of their own, so a ground with none — and the
 * map editor, which is never handed a private pin — is unchanged.
 *
 * Every count in this panel is an ITEM count (pins plus lines), because a
 * folder holds both. One unit means the heading, the collection headings and
 * the row counts add up wherever a reader checks them.
 *
 * ── Every management affordance is opt-in ──────────────────────────────────
 * Each write is an optional prop, and the control for it renders only when the
 * prop is there. That is what lets one component serve the prototype (mock
 * handlers, everything live) and production (only what the API can actually do)
 * without a flag, and it is why a view-only member never sees a dead button:
 * the caller passes nothing and the affordance is absent rather than disabled.
 *
 * ── A conflict worth knowing about ─────────────────────────────────────────
 * {@link DeleteFolderDialog} offers "keep the pins" / "delete the pins too",
 * as the design draws it. CLUB-676's own correction says the opposite: the app
 * permits deleting only an EMPTY folder, and the web API answers 422 for one
 * that still holds pins. Neither arm of that dialog is implementable against
 * the built backend today. It runs here against mock state so the design can be
 * reviewed; it must be settled before this reaches production.
 */

export type PinVisibilityControls = {
  hiddenPinIds: ReadonlySet<string>;
  onSetPinHidden: (id: string, hidden: boolean) => void;
  collectionHidden: boolean;
  onSetCollectionHidden: (hidden: boolean) => void;
};

export type PinFolderTreeProps = {
  folders: readonly PinFolder[];
  pins: readonly MapObject[];
  lines?: readonly GeoLine[];
  /** Centre the map on an item. */
  onFocus?: (point: GeoPt) => void;
  /**
   * Select an item, where the host has a notion of selection — the map editor
   * does, and the spec asks the two to agree: "clicking a row selects that pin
   * on the map; the map selection checks the row back".
   */
  onSelect?: (id: string) => void;
  selectedId?: string | null;
  visibility?: PinVisibilityControls;
  maxHeight?: number;
  /** Skeleton rows instead of a list. 521 rows arrive in one request. */
  loading?: boolean;
  /** Ground name and size, for the empty state's subtitle. */
  groundLabel?: string;

  // ── Writes. Absent → the affordance is not rendered. ──
  onCreateFolder?: (name: string) => void;
  onRenameFolder?: (folderId: string, name: string) => void;
  onDeleteFolder?: (folderId: string, opts: { deletePins: boolean }) => void;
  /** `folderId` null files the pins at the root. */
  onMovePins?: (ids: readonly string[], folderId: string | null) => void;
  onCopyPins?: (ids: readonly string[], folderId: string | null) => void;
  onDeletePins?: (ids: readonly string[]) => void;
  onEditPin?: (id: string) => void;
  onAddPin?: () => void;
  onImportGpx?: () => void;
  onExportGpx?: (folderId?: string) => void;
};

/**
 * The management surface as one bag, for a host that forwards it wholesale —
 * `TeamDetailPage` takes this and hands it straight to the panel, so adding a
 * write later does not mean threading another prop through the page.
 */
export type PinCollectionOps = Pick<
  PinFolderTreeProps,
  | 'onCreateFolder' | 'onRenameFolder' | 'onDeleteFolder'
  | 'onMovePins' | 'onCopyPins' | 'onDeletePins'
  | 'onEditPin' | 'onAddPin' | 'onImportGpx' | 'onExportGpx'
  | 'visibility' | 'loading' | 'groundLabel'
>;

/**
 * How many rows one folder renders before the rest go behind "show all".
 * A Tracker import can drop 500+ pins into one folder; nobody reads row 300.
 */
const ROWS_BEFORE_TRUNCATION = 100;

/** Stable empty set, so a host with no visibility controls allocates nothing. */
const EMPTY_IDS: ReadonlySet<string> = new Set();

/** Everything one collection renders a row for, as an item count. */
const collectionItems = (c: PinFolderCollection): number =>
  c.folders.reduce((n, f) => n + f.pins.length + f.lines.length, 0) +
  c.ungroupedPins.length +
  c.ungroupedLines.length;

/** One collection's pins, flat — folders first, then the unfiled ones. */
const collectionPins = (c: PinFolderCollection): MapObject[] => [
  ...c.folders.flatMap((f) => f.pins),
  ...c.ungroupedPins,
];

/** One collection's lines, flat. Empty for the viewer's own — see PinFolderView. */
const collectionLines = (c: PinFolderCollection): GeoLine[] => [
  ...c.folders.flatMap((f) => f.lines),
  ...c.ungroupedLines,
];

/** Every id one collection renders a row for, in row order. */
const collectionIds = (c: PinFolderCollection): string[] => [
  ...c.folders.flatMap((f) => [...f.pins.map((p) => p.id), ...f.lines.map((l) => l.id)]),
  ...c.ungroupedPins.map((p) => p.id),
  ...c.ungroupedLines.map((l) => l.id),
];

/**
 * Row state — which folders are expanded, which are fully shown, what is being
 * dragged over — is keyed on the (collection, id) PAIR for the same reason the
 * view's grouping is: a team folder and a private folder can carry the same id,
 * and keying on the id alone would expand both at once. `__root__` is the
 * ungrouped bucket, which exists once per collection.
 */
const rowKey = (isPrivate: boolean, id: string) => `${isPrivate ? 'p' : 't'}:${id}`;

const lineKindLabels =
  (t: (key: string) => string): LineKindLabels =>
  (kind) =>
    kind === 'arrow' ? t('lines.arrowNounCap') : kind === 'trail' ? t('lines.trailNounCap') : t('lines.lineNounCap');

type DialogState =
  | { kind: 'newFolder' }
  | { kind: 'renameFolder'; folderId: string }
  | { kind: 'deleteFolder'; folderId: string }
  | { kind: 'deletePins'; ids: string[] }
  | { kind: 'movePins'; ids: string[]; mode: 'move' | 'copy'; fromFolderId?: string | null };

export function PinFolderTree(props: PinFolderTreeProps) {
  const {
    folders,
    pins,
    lines = [],
    onFocus,
    onSelect,
    selectedId,
    visibility,
    maxHeight,
    loading = false,
    groundLabel,
    onCreateFolder,
    onRenameFolder,
    onDeleteFolder,
    onMovePins,
    onCopyPins,
    onDeletePins,
    onEditPin,
    onAddPin,
    onImportGpx,
    onExportGpx,
  } = props;

  const { t } = useTranslation('map');
  const catalog = usePinTypeCatalog();
  const [query, setQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<PinSortOrder | undefined>(undefined);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [fullyShown, setFullyShown] = useState<ReadonlySet<string>>(new Set());
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [menu, setMenu] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  const lineLabels = useMemo(() => lineKindLabels(t), [t]);
  const view = useMemo(
    () => buildPinFolderView({
      pins, lines, folders, searchQuery: query, previousSortOrder: sortOrder, lineLabels, labelOf: catalog.label,
    }),
    [pins, lines, folders, query, sortOrder, lineLabels, catalog],
  );

  const searching = query.trim().length > 0;
  // ITEMS, not pins, everywhere a number appears in this panel — the heading,
  // the collection headings, the folder rows and the ungrouped rows. A folder
  // holds lines as well as pins, so the row counts have always been item
  // counts; the heading counting pins alone is what left it reading "15 pins"
  // above rows adding up to 17. One unit means the arithmetic closes wherever
  // a reader checks it.
  const totalItems = pins.length + lines.length;
  const shownItems = collectionItems(view.team) + collectionItems(view.mine);
  // NOT while loading. An empty response and an unarrived one look identical
  // from here, and "No pins yet" is a claim about data nobody has seen.
  const empty = !loading && pins.length === 0 && lines.length === 0 && folders.length === 0;
  // The two collections are told apart on screen only when the viewer actually
  // has pins of their own. A ground with none — and the map editor, which is
  // never handed a private pin — renders exactly as it did before the split,
  // with no heading in front of a single list.
  const splitCollections = collectionItems(view.mine) > 0 || view.mine.folders.length > 0;

  const canManageFolders = Boolean(onCreateFolder || onRenameFolder || onDeleteFolder);
  const canPick = Boolean(onMovePins || onCopyPins || onDeletePins);

  const nameOf = (id: string) => {
    const pin = pins.find((p) => p.id === id);
    if (pin) return pinDisplayName(pin, catalog.label);
    const line = lines.find((l) => l.id === id);
    return line ? lineDisplayName(line, lineLabels) : '';
  };
  const folderById = (id: string) => folders.find((f) => f.id === id);
  const folderLabel = (f: PinFolder | undefined) =>
    (f?.name ?? '').trim() || t('pinFolders.unnamedFolder');
  // Membership matches on the (collection, folder id) PAIR, never the id alone:
  // a team folder and a private folder can carry the same id, so matching on
  // the id would report a hunter's pins as the team folder's contents. Lines
  // are the team's only — nothing writes an owner onto one.
  const pinsIn = (folder: PinFolder) =>
    pins.filter((p) => p.folderId === folder.id && (p.isPrivate === true) === (folder.private === true));
  const linesIn = (folder: PinFolder) =>
    folder.private === true ? [] : lines.filter((l) => l.folderId === folder.id);
  const itemsIn = (folder: PinFolder | undefined) =>
    folder == null ? 0 : pinsIn(folder).length + linesIn(folder).length;
  const folderCounts = useMemo(
    () => Object.fromEntries(folders.map((f) => [f.id, itemsIn(f)])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [folders, pins, lines],
  );

  const toggle = (set: ReadonlySet<string>, id: string): ReadonlySet<string> => {
    const next = new Set(set);
    if (!next.delete(id)) next.add(id);
    return next;
  };

  // While searching, a folder holding a match opens itself: the point of typing
  // is to see the hits, and leaving them behind a collapsed header reads as
  // "no results".
  const isExpanded = (key: string, hasMatches: boolean) =>
    (searching && hasMatches) || expanded.has(key);

  const collectionOff = visibility?.collectionHidden ?? false;

  const exitPicking = () => {
    setPicking(false);
    setPicked(new Set());
  };

  const allVisibleIds = useMemo(
    () => [...collectionIds(view.team), ...collectionIds(view.mine)],
    [view],
  );

  /** Which collection the dragged item belongs to, so a drop target in the
   *  other one is never offered: a pin can only be filed into a folder of its
   *  own collection, and the team endpoints refuse a pin the team does not own.
   *  A line is the team's, hence the `?? false`. */
  const dragPrivate = dragId == null ? false : pins.find((p) => p.id === dragId)?.isPrivate ?? false;

  /** Drops `id` into `folderId` (null = the root). Used by drag and by menus. */
  const fileInto = (id: string, folderId: string | null) => {
    onMovePins?.([id], folderId);
    setDragId(null);
    setDropTarget(null);
    if (folderId) setExpanded((s) => new Set(s).add(rowKey(dragPrivate, folderId)));
  };

  // ── Dialogs ──────────────────────────────────────────────────────────────
  const dialogNode = (() => {
    if (!dialog) return null;
    const close = () => setDialog(null);

    switch (dialog.kind) {
      case 'newFolder':
        return (
          <FolderNameDialog
            mode="create"
            taken={folders.map((f) => f.name)}
            onCancel={close}
            onSubmit={(name) => {
              onCreateFolder?.(name);
              close();
            }}
          />
        );
      case 'renameFolder': {
        const folder = folderById(dialog.folderId);
        return (
          <FolderNameDialog
            mode="rename"
            initialName={folder?.name ?? ''}
            taken={folders.filter((f) => f.id !== dialog.folderId).map((f) => f.name)}
            onCancel={close}
            onSubmit={(name) => {
              onRenameFolder?.(dialog.folderId, name);
              close();
            }}
          />
        );
      }
      case 'deleteFolder':
        return (
          <DeleteFolderDialog
            folderName={folderLabel(folderById(dialog.folderId))}
            pinCount={itemsIn(folderById(dialog.folderId))}
            allowDeletePins={Boolean(onDeletePins)}
            onCancel={close}
            onConfirm={(opts) => {
              onDeleteFolder?.(dialog.folderId, opts);
              close();
            }}
          />
        );
      case 'deletePins':
        return (
          <DeletePinsDialog
            names={dialog.ids.map(nameOf)}
            onCancel={close}
            onConfirm={() => {
              onDeletePins?.(dialog.ids);
              exitPicking();
              close();
            }}
          />
        );
      case 'movePins':
        return (
          <MovePinsDialog
            mode={dialog.mode}
            count={dialog.ids.length}
            folders={folders}
            folderCounts={folderCounts}
            currentFolderId={dialog.fromFolderId}
            onCancel={close}
            onConfirm={(folderId) => {
              (dialog.mode === 'move' ? onMovePins : onCopyPins)?.(dialog.ids, folderId);
              exitPicking();
              close();
            }}
          />
        );
    }
  })();

  // ── Menus ────────────────────────────────────────────────────────────────
  // Groups match the design's three bands. The first two repeat the toolbar's
  // own buttons on purpose — the same reasoning the spec gives for "Show on
  // map" appearing beside the locate button: a keyboard user reaches the menu,
  // not the icon row.
  // On the toolbar directly, never behind a "More". Both surfaces carry only a
  // couple of these, and a menu in front of two buttons is a click in front of
  // a click. Row and folder kebabs still use PinMenu — those are per-item and
  // there is nowhere to put six actions on a 40px row.
  const panelActions: MenuItem[] = [
    onCreateFolder && {
      id: 'new', group: 0, icon: 'create_new_folder', label: t('pinFolders.newFolder'),
      onSelect: () => setDialog({ kind: 'newFolder' }),
    },
    canPick && {
      id: 'pick', group: 0, icon: 'edit', label: t('pinFolders.edit'),
      onSelect: () => setPicking(true),
    },
    onImportGpx && { id: 'import', group: 1, icon: 'download', label: t('pinFolders.importGpx'), onSelect: onImportGpx },
    onExportGpx && { id: 'export', group: 1, icon: 'share', label: t('pinFolders.exportGpx'), onSelect: () => onExportGpx() },
    // No "Show all pins" entry: the header's eye already toggles the whole
    // collection, both ways, and a one-way copy of it would sit beside its own
    // duplicate.
  ].filter(Boolean) as MenuItem[];

  /**
   * Whether a folder reads as shown, by the same rule its eye uses, applied to
   * the WHOLE folder — which is what the menu entry acts on, search or no
   * search. Deriving the label from anything else would let the menu say
   * "Hide" and then unhide, or the reverse.
   */
  const folderShown = (folder: PinFolder): boolean =>
    folderVisible(pinsIn(folder), linesIn(folder), visibility?.hiddenPinIds ?? EMPTY_IDS);

  const folderMenuItems = (folder: PinFolder): MenuItem[] =>
    [
      onRenameFolder && {
        id: 'rename', group: 0, icon: 'edit', label: t('pinFolders.rename'),
        onSelect: () => setDialog({ kind: 'renameFolder', folderId: folder.id }),
      },
      onMovePins && {
        id: 'move', group: 0, icon: 'drive_file_move', label: t('pinFolders.movePinsTo'),
        onSelect: () =>
          setDialog({
            kind: 'movePins', mode: 'move', fromFolderId: folder.id,
            ids: pinsIn(folder).map((p) => p.id),
          }),
      },
      onCopyPins && {
        id: 'copy', group: 0, icon: 'content_copy', label: t('pinFolders.copyPinsTo'),
        onSelect: () =>
          setDialog({
            kind: 'movePins', mode: 'copy', fromFolderId: folder.id,
            ids: pinsIn(folder).map((p) => p.id),
          }),
      },
      visibility && {
        id: 'hide',
        group: 1,
        icon: folderShown(folder) ? 'visibility_off' : 'visibility',
        label: folderShown(folder) ? t('pinFolders.hideOnMap') : t('pinFolders.showOnMap'),
        onSelect: () => {
          const hidden = folderShown(folder);
          for (const p of pinsIn(folder)) visibility.onSetPinHidden(p.id, hidden);
          for (const l of linesIn(folder)) visibility.onSetPinHidden(l.id, hidden);
        },
      },
      onExportGpx && {
        id: 'export', group: 1, icon: 'share', label: t('pinFolders.exportFolder'),
        onSelect: () => onExportGpx(folder.id),
      },
      onDeleteFolder && {
        id: 'delete', group: 2, icon: 'delete', label: t('pinFolders.deleteFolder'), destructive: true,
        onSelect: () => setDialog({ kind: 'deleteFolder', folderId: folder.id }),
      },
    ].filter(Boolean) as MenuItem[];

  const pinMenuItems = (item: { id: string; point?: GeoPt; folderId?: string }): MenuItem[] =>
    [
      onFocus && item.point && {
        // The same action as the row's locate button, repeated here for
        // keyboard users — the spec's note on the folder menu.
        //
        // Named for what it does, which is move the map. It read "Show on map"
        // beside a "Hide on map" entry that meant something else entirely, so
        // the pair looked like one toggle and neither half was the other's
        // inverse; "Show on map" is now the visibility entry below.
        id: 'centre', group: 0, icon: 'target', label: t('pinFolders.centreOn'),
        onSelect: () => onFocus(item.point!),
      },
      onEditPin && { id: 'edit', group: 0, icon: 'edit', label: t('pinFolders.editPin'), onSelect: () => onEditPin(item.id) },
      onMovePins && {
        id: 'move', group: 1, icon: 'drive_file_move', label: t('pinFolders.moveToFolder'),
        onSelect: () => setDialog({ kind: 'movePins', mode: 'move', ids: [item.id], fromFolderId: item.folderId ?? null }),
      },
      onCopyPins && {
        id: 'copy', group: 1, icon: 'content_copy', label: t('pinFolders.copyToFolder'),
        onSelect: () => setDialog({ kind: 'movePins', mode: 'copy', ids: [item.id], fromFolderId: item.folderId ?? null }),
      },
      // Both ways. A one-way hide was a dead end: the row carries no eye of its
      // own (see the note where the trailing controls are rendered), an item in
      // no folder has no folder switch either, and the collection master is a
      // separate flag that leaves a per-pin hide in place when it is turned back
      // on — so a hidden pin could not be brought back at all.
      visibility && {
        id: 'hide',
        group: 1,
        icon: visibility.hiddenPinIds.has(item.id) ? 'visibility' : 'visibility_off',
        label: visibility.hiddenPinIds.has(item.id) ? t('pinFolders.showOnMap') : t('pinFolders.hideOnMap'),
        onSelect: () => visibility.onSetPinHidden(item.id, !visibility.hiddenPinIds.has(item.id)),
      },
      onDeletePins && {
        id: 'delete', group: 2, icon: 'delete', label: t('pinFolders.deletePin'), destructive: true,
        onSelect: () => setDialog({ kind: 'deletePins', ids: [item.id] }),
      },
    ].filter(Boolean) as MenuItem[];

  // ── Render ───────────────────────────────────────────────────────────────
  const sortOptions = [
    ...(pins.some((p) => p.orderIndex != null) ? [{ value: 'custom', label: t('pinFolders.sortCustom') }] : []),
    { value: 'ascending', label: t('pinFolders.sortAsc') },
    { value: 'descending', label: t('pinFolders.sortDesc') },
  ];

  /**
   * One collection's rows: its folders, then whatever it holds outside them.
   * Both collections render through this, so the team's pins and the viewer's
   * own cannot drift into two different-looking lists.
   */
  const renderCollection = (collection: PinFolderCollection) => {
    const isPrivate = collection.private;
    const rootKey = rowKey(isPrivate, '__root__');
    const droppable = Boolean(dragId) && dragPrivate === isPrivate;
    return (
      <>
        {collection.folders.map(({ folder, pins: folderPins, lines: folderLines }) => {
          // While searching, a folder with no hits drops out entirely —
          // the spec's rule. Outside search it stays, empty and all.
          if (searching && folderPins.length + folderLines.length === 0) return null;
          const count = folderPins.length + folderLines.length;
          const key = rowKey(isPrivate, folder.id);
          const open = isExpanded(key, count > 0);
          const menuItems = folderMenuItems(folder);
          return (
            <div key={key}>
              <FolderRow
                name={folderLabel(folder)}
                unnamed={folder.name.trim().length === 0}
                count={count}
                open={open}
                dropActive={dropTarget === key}
                onToggle={() => setExpanded((s) => toggle(s, key))}
                onDragOver={droppable ? () => setDropTarget(key) : undefined}
                onDragLeave={() => setDropTarget((d) => (d === key ? null : d))}
                onDrop={droppable ? () => fileInto(dragId!, folder.id) : undefined}
                visibilitySlot={
                  visibility && (
                    <VisibilityButton
                      on={folderVisible(folderPins, folderLines, visibility.hiddenPinIds)}
                      label={t('pinFolders.folderSwitchAria', { name: folderLabel(folder) })}
                      onToggle={(next) => {
                        for (const p of folderPins) visibility.onSetPinHidden(p.id, !next);
                        for (const l of folderLines) visibility.onSetPinHidden(l.id, !next);
                      }}
                    />
                  )
                }
                menuSlot={
                  menuItems.length > 0 && !picking ? (
                    <RowMenuButton
                      open={menu === `folder:${key}`}
                      label={t('pinFolders.folderMenuAria', { name: folderLabel(folder) })}
                      items={menuItems}
                      onToggle={() => setMenu((m) => (m === `folder:${key}` ? null : `folder:${key}`))}
                      onClose={() => setMenu(null)}
                    />
                  ) : null
                }
              />
              {open && (
                <ItemRows
                  pins={folderPins}
                  lines={folderLines}
                  indent
                  folderId={folder.id}
                  showAll={fullyShown.has(key)}
                  onShowAll={() => setFullyShown((s) => toggle(s, key))}
                  // Always the plain empty text: a folder with no matches
                  // is dropped from the list above rather than rendered
                  // empty, so "no matches in this folder" is unreachable.
                  emptyText={t('pinFolders.emptyFolder')}
                  {...{
                    onFocus, onSelect, selectedId, visibility, collectionOff, lineLabels,
                    picking, picked, setPicked, menu, setMenu, pinMenuItems,
                    dragId, setDragId, t, labelOf: catalog.label,
                  }}
                />
              )}
            </div>
          );
        })}

        {(collection.ungroupedPins.length > 0 || collection.ungroupedLines.length > 0 || droppable) && (
          <div>
            {collection.folders.length > 0 && (
              <SectionHeading
                text={t('pinFolders.ungrouped')}
                count={collection.ungroupedPins.length + collection.ungroupedLines.length}
                // The same control a folder row carries, for the same
                // reason: these pins are a group on screen, and hiding one
                // of them had no group-level way back.
                visibilitySlot={
                  visibility && (
                    <VisibilityButton
                      on={folderVisible(collection.ungroupedPins, collection.ungroupedLines, visibility.hiddenPinIds)}
                      label={t('pinFolders.ungroupedSwitchAria')}
                      onToggle={(next) => {
                        for (const p of collection.ungroupedPins) visibility.onSetPinHidden(p.id, !next);
                        for (const l of collection.ungroupedLines) visibility.onSetPinHidden(l.id, !next);
                      }}
                    />
                  )
                }
                // Dropping here takes a pin OUT of its folder — the design's
                // wording, and the only way out that is not a delete.
                dropActive={dropTarget === rootKey}
                onDragOver={droppable ? () => setDropTarget(rootKey) : undefined}
                onDragLeave={() => setDropTarget((d) => (d === rootKey ? null : d))}
                onDrop={droppable ? () => fileInto(dragId!, null) : undefined}
              />
            )}
            <ItemRows
              pins={collection.ungroupedPins}
              lines={collection.ungroupedLines}
              showAll={fullyShown.has(rootKey)}
              onShowAll={() => setFullyShown((s) => toggle(s, rootKey))}
              {...{
                onFocus, onSelect, selectedId, visibility, collectionOff, lineLabels,
                picking, picked, setPicked, menu, setMenu, pinMenuItems,
                dragId, setDragId, t, labelOf: catalog.label,
              }}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <section
      aria-label={t('pinFolders.title')}
      style={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        minHeight: 0,
        flex: 1,
        background: COLORS.surfaceContainerLowest,
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ padding: '14px 16px 10px' }}>
        <div
          style={{
            fontSize: 11, fontWeight: 700, color: COLORS.outline,
            textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4,
          }}
        >
          {t('pinFolders.tag')}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 21, fontWeight: 500, lineHeight: 1.15 }}>
              {loading
                ? t('pinFolders.headingLoading')
                : empty
                ? t('pinFolders.headingEmpty')
                : searching
                ? t('pinFolders.headingSearch', { count: shownItems, query: query.trim() })
                : t('pinFolders.heading', { count: totalItems })}
            </div>
            <div style={{ fontSize: 12, color: COLORS.onSurfaceVariant, marginTop: 4, lineHeight: 1.5 }}>
              {loading
                ? ''
                : empty
                ? groundLabel ?? ''
                : searching
                ? t('pinFolders.subtitleSearch')
                : folders.length === 0
                ? t('pinFolders.subtitleNoFolders')
                : t('pinFolders.subtitle', { count: folders.length })}
            </div>
          </div>
          {visibility && !picking && (
            <button
              type="button"
              className="wh-btn-icon"
              onClick={() => visibility.onSetCollectionHidden(!collectionOff)}
              title={collectionOff ? t('pinFolders.showAllPins') : t('pinFolders.hideAllPins')}
              aria-label={collectionOff ? t('pinFolders.showAllPins') : t('pinFolders.hideAllPins')}
              aria-pressed={!collectionOff}
            >
              <Icon name={collectionOff ? 'visibility_off' : 'visibility'} size={20} />
            </button>
          )}
        </div>
      </div>

      {/* ── Toolbar, or the selection bar that replaces it ─────────────── */}
      <div style={{ padding: '0 16px 12px' }}>
        {picking ? (
          <SelectionBar
            count={picked.size}
            allSelected={picked.size > 0 && picked.size === allVisibleIds.length}
            onExit={exitPicking}
            onSelectAll={() =>
              setPicked((s) => (s.size === allVisibleIds.length ? new Set() : new Set(allVisibleIds)))
            }
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <SearchField value={query} onChange={setQuery} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* '100%' rather than a px width: the select shares its row
                    with the menu button, so it has to take whatever is left
                    over at whatever width the card is. */}
                <WaSelect
                  value={view.sortOrder}
                  options={sortOptions}
                  onChange={(v) => setSortOrder(v as PinSortOrder)}
                  ariaLabel={t('pinFolders.sortAria')}
                  width="100%"
                />
              </div>
              {panelActions.map((action) => (
                <ToolbarButton
                  key={action.id}
                  icon={action.icon}
                  label={action.label}
                  onClick={action.onSelect}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── List ───────────────────────────────────────────────────────── */}
      <div style={{ overflowY: 'auto', maxHeight, flex: 1, minHeight: 0, borderTop: `1px solid ${COLORS.surfaceContainerHighest}` }}>
        {loading ? (
          <SkeletonRows />
        ) : empty ? (
          <EmptyState onAddPin={onAddPin} onImportGpx={onImportGpx} />
        ) : searching && shownItems === 0 ? (
          <EmptyNote text={t('pinFolders.emptySearch', { query: query.trim() })} />
        ) : (
          <>
            {[view.team, view.mine].map((collection) => {
              // While searching, a collection with no hits drops out heading and
              // all — the same rule the folder rows below follow, for the same
              // reason: an empty labelled section reads as a broken filter.
              if (searching && collectionItems(collection) === 0) return null;
              return (
                <div key={collection.private ? 'mine' : 'team'}>
                  {splitCollections && (
                    <CollectionHeading
                      icon={collection.private ? 'user' : 'group'}
                      text={collection.private ? t('pinFolders.myCollection') : t('pinFolders.teamCollection')}
                      count={collectionItems(collection)}
                      visibilitySlot={
                        visibility && (
                          <VisibilityButton
                            on={folderVisible(
                              collectionPins(collection),
                              collectionLines(collection),
                              visibility.hiddenPinIds,
                            )}
                            label={t('pinFolders.collectionSwitchAria', {
                              name: collection.private
                                ? t('pinFolders.myCollection')
                                : t('pinFolders.teamCollection'),
                            })}
                            onToggle={(next) => {
                              for (const p of collectionPins(collection)) visibility.onSetPinHidden(p.id, !next);
                              for (const l of collectionLines(collection)) visibility.onSetPinHidden(l.id, !next);
                            }}
                          />
                        )
                      }
                    />
                  )}
                  {renderCollection(collection)}
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* ── Bulk actions, while picking ────────────────────────────────── */}
      {picking && (
        <BulkActions
          disabled={picked.size === 0}
          ids={[...picked]}
          onMove={onMovePins ? (ids) => setDialog({ kind: 'movePins', mode: 'move', ids }) : undefined}
          onCopy={onCopyPins ? (ids) => setDialog({ kind: 'movePins', mode: 'copy', ids }) : undefined}
          onHide={
            visibility ? (ids) => ids.forEach((id) => visibility.onSetPinHidden(id, true)) : undefined
          }
          onDelete={onDeletePins ? (ids) => setDialog({ kind: 'deletePins', ids }) : undefined}
        />
      )}

      {dialogNode}
    </section>
  );
}

/**
 * Whether a folder reads as visible. Derived, never stored — the app has no
 * per-folder hidden flag, so a folder is on when anything inside it is on, and
 * an EMPTY folder is on because there is nothing in it to be hidden.
 */
function folderVisible(
  pins: readonly MapObject[],
  lines: readonly GeoLine[],
  hidden: ReadonlySet<string>,
): boolean {
  if (pins.length === 0 && lines.length === 0) return true;
  return pins.some((p) => !hidden.has(p.id)) || lines.some((l) => !hidden.has(l.id));
}

// ── Toolbar pieces ─────────────────────────────────────────────────────────

function SearchField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation('map');
  return (
    <div style={{ position: 'relative' }}>
      <Icon
        name="search"
        size={18}
        color={COLORS.outline}
        style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('pinFolders.searchPlaceholder')}
        aria-label={t('pinFolders.searchAria')}
        style={{
          width: '100%', height: 36, boxSizing: 'border-box',
          padding: '0 10px 0 34px', fontSize: 14, fontFamily: 'inherit',
          color: COLORS.onSurface, background: COLORS.surfaceContainerLowest,
          border: `1px solid ${COLORS.outlineVariant}`, borderRadius: 8,
        }}
      />
    </div>
  );
}

const ToolbarButton = forwardRef<
  HTMLButtonElement,
  { icon: string; label: string; onClick: () => void; expanded?: boolean }
>(function ToolbarButton({ icon, label, onClick, expanded }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className="wh-btn-icon"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-expanded={expanded}
      style={{ width: 36, height: 36, flexShrink: 0, background: expanded ? COLORS.surfaceContainerHigh : undefined }}
    >
      <Icon name={icon} size={20} />
    </button>
  );
});

/**
 * The contextual bar that replaces the search row while picking — the pattern
 * DocumentsTab already uses, in `primary` so there is no doubt the panel is in
 * a different mode.
 */
function SelectionBar({
  count, allSelected, onExit, onSelectAll,
}: { count: number; allSelected: boolean; onExit: () => void; onSelectAll: () => void }) {
  const { t } = useTranslation('map');
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 8, height: 44,
        padding: '0 6px', borderRadius: 8, background: COLORS.primary,
      }}
    >
      <button
        type="button"
        onClick={onExit}
        title={t('pinFolders.exitSelection')}
        aria-label={t('pinFolders.exitSelection')}
        style={{
          width: 36, height: 36, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: 'none', background: 'none', borderRadius: 999, cursor: 'pointer', color: COLORS.onPrimary,
        }}
      >
        <Icon name="close" size={20} />
      </button>
      <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: COLORS.onPrimary }}>
        {t('pinFolders.nSelected', { count })}
      </span>
      <button
        type="button"
        onClick={onSelectAll}
        style={{
          height: 26, padding: '0 10px', flexShrink: 0,
          borderRadius: 999, border: '1px solid rgba(255,255,255,0.5)', background: 'none',
          color: COLORS.onPrimary, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
        }}
      >
        {allSelected ? t('pinFolders.selectNone') : t('pinFolders.selectAll')}
      </button>
    </div>
  );
}

/** The footer while picking. Sits below the list so the count stays readable. */
function BulkActions({
  ids, disabled, onMove, onCopy, onHide, onDelete,
}: {
  ids: string[];
  disabled: boolean;
  onMove?: (ids: string[]) => void;
  onCopy?: (ids: string[]) => void;
  onHide?: (ids: string[]) => void;
  onDelete?: (ids: string[]) => void;
}) {
  const { t } = useTranslation('map');
  const actions = [
    onMove && { id: 'move', icon: 'drive_file_move', label: t('pinFolders.moveTo'), run: onMove },
    onCopy && { id: 'copy', icon: 'content_copy', label: t('pinFolders.copyTo'), run: onCopy },
    onHide && { id: 'hide', icon: 'visibility_off', label: t('pinFolders.hide'), run: onHide },
    onDelete && { id: 'delete', icon: 'delete', label: t('pinFolders.delete'), run: onDelete, destructive: true },
  ].filter(Boolean) as { id: string; icon: string; label: string; run: (ids: string[]) => void; destructive?: boolean }[];

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 2, padding: '6px 8px',
        background: COLORS.surfaceContainerLow, borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
      }}
    >
      {actions.map((a) => (
        <button
          key={a.id}
          type="button"
          disabled={disabled}
          onClick={() => a.run(ids)}
          style={{
            flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 3, padding: '6px 0',
            border: 'none', background: 'none', borderRadius: 8,
            cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit',
            opacity: disabled ? 0.4 : 1,
            color: a.destructive ? COLORS.error : COLORS.primary,
          }}
        >
          <Icon name={a.icon} size={18} />
          <span style={{ fontSize: 11, fontWeight: 600 }}>{a.label}</span>
        </button>
      ))}
    </div>
  );
}

// ── Rows ───────────────────────────────────────────────────────────────────

function FolderRow({
  name, unnamed, count, open, onToggle,
  visibilitySlot, menuSlot, dropActive, onDragOver, onDragLeave, onDrop,
}: {
  name: string;
  unnamed: boolean;
  count: number;
  open: boolean;
  onToggle: () => void;
  visibilitySlot?: ReactNode;
  menuSlot?: ReactNode;
  dropActive?: boolean;
  onDragOver?: () => void;
  onDragLeave?: () => void;
  onDrop?: () => void;
}) {
  const { t } = useTranslation('map');
  return (
    <div
      onDragOver={onDragOver ? (e) => { e.preventDefault(); onDragOver(); } : undefined}
      onDragLeave={onDragLeave}
      onDrop={onDrop ? (e) => { e.preventDefault(); onDrop(); } : undefined}
      className="wh-row"
      style={{
        display: 'flex', alignItems: 'center', gap: 8, minHeight: 44,
        padding: '0 10px 0 12px',
        // Tinted, so a folder header reads as a band across the list rather
        // than as another row in it.
        background: dropActive ? COLORS.primaryContainer : COLORS.surfaceContainerLow,
        // Dashed only while a pin hovers it, so the drop target is unmistakable
        // without every folder looking provisional at rest.
        outline: dropActive ? `2px dashed ${COLORS.primary}` : undefined,
        outlineOffset: -2,
        borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
        // Keeps the header in view while its pins scroll past under it.
        position: 'sticky', top: 0, zIndex: 2,
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-label={t('pinFolders.folderRowAria', { name, count })}
        style={{
          flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 4px', background: 'none', border: 'none',
          cursor: 'pointer', fontFamily: 'inherit', color: 'inherit',
        }}
      >
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={18} color={COLORS.outline} />
        <Icon name={open ? 'folder_open' : 'folder'} size={18} color={COLORS.outline} />
        <span
          style={{
            flex: 1, minWidth: 0, fontSize: 11, fontWeight: 700, letterSpacing: 0.8,
            textTransform: 'uppercase',
            color: unnamed ? COLORS.outline : COLORS.onSurfaceVariant,
            fontStyle: unnamed ? 'italic' : undefined,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left',
          }}
        >
          {name}
        </span>
        <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.onSurfaceVariant, flexShrink: 0 }}>{count}</span>
      </button>
      {visibilitySlot}
      {menuSlot}
    </div>
  );
}

type RowsShared = {
  onFocus?: (p: GeoPt) => void;
  onSelect?: (id: string) => void;
  selectedId?: string | null;
  visibility?: PinVisibilityControls;
  collectionOff: boolean;
  lineLabels: LineKindLabels;
  picking: boolean;
  picked: ReadonlySet<string>;
  setPicked: (fn: (s: ReadonlySet<string>) => ReadonlySet<string>) => void;
  menu: string | null;
  setMenu: (fn: (m: string | null) => string | null) => void;
  pinMenuItems: (item: { id: string; point?: GeoPt; folderId?: string }) => MenuItem[];
  dragId: string | null;
  setDragId: (id: string | null) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
  labelOf: PinTypeLabeler;
};

function ItemRows({
  pins, lines, indent = false, folderId, showAll, onShowAll, emptyText, ...shared
}: RowsShared & {
  pins: readonly MapObject[];
  lines: readonly GeoLine[];
  indent?: boolean;
  folderId?: string;
  showAll: boolean;
  onShowAll: () => void;
  emptyText?: string;
}) {
  const { t, labelOf } = shared;
  const total = pins.length + lines.length;
  if (total === 0) return emptyText ? <EmptyNote text={emptyText} indent={indent} /> : null;

  const limit = showAll ? Number.POSITIVE_INFINITY : ROWS_BEFORE_TRUNCATION;
  const shownPins = pins.slice(0, limit);
  const shownLines = lines.slice(0, Math.max(0, limit - shownPins.length));
  const hidden = total - shownPins.length - shownLines.length;

  return (
    <>
      {shownPins.map((pin) => (
        <ItemRow
          key={pin.id}
          id={pin.id}
          name={pinDisplayName(pin, labelOf)}
          glyph={<PinGlyph kind={pin.type} color={pin.color} symbol={pin.customSymbol} size={24} />}
          point={{ lat: pin.lat, lon: pin.lon }}
          folderId={folderId}
          indent={indent}
          {...shared}
        />
      ))}
      {shownLines.map((line) => (
        <ItemRow
          key={line.id}
          id={line.id}
          name={lineDisplayName(line, shared.lineLabels)}
          glyph={
            <Icon name={line.kind === 'arrow' ? 'arrow-right' : 'edit'} size={24} color={line.color ?? COLORS.outline} />
          }
          point={line.points[0]}
          folderId={folderId}
          indent={indent}
          {...shared}
        />
      ))}
      {hidden > 0 && (
        <button
          type="button"
          onClick={onShowAll}
          style={{
            width: '100%', padding: `10px 16px 10px ${indent ? 44 : 16}px`,
            fontSize: 12, fontWeight: 600, color: COLORS.primary, textAlign: 'left',
            background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
            borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
          }}
        >
          {t('pinFolders.showAll', { count: hidden })}
        </button>
      )}
    </>
  );
}

function ItemRow({
  id, name, glyph, point, folderId, indent,
  onFocus, onSelect, selectedId, visibility, collectionOff,
  picking, picked, setPicked, menu, setMenu, pinMenuItems, dragId, setDragId, t,
}: RowsShared & {
  id: string;
  name: string;
  glyph: ReactNode;
  point?: GeoPt;
  folderId?: string;
  indent?: boolean;
}) {
  const hidden = visibility?.hiddenPinIds.has(id) ?? false;
  const dimmed = collectionOff || hidden;
  const checked = picked.has(id);
  const selected = selectedId === id;
  const menuItems = pinMenuItems({ id, point, folderId });

  const label = (
    <>
      <span style={{ opacity: dimmed ? 0.4 : 1, display: 'inline-flex' }}>{glyph}</span>
      <span
        style={{
          flex: 1, minWidth: 0, fontSize: 14, fontWeight: 500,
          color: dimmed ? COLORS.outline : COLORS.onSurface,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left',
        }}
      >
        {name}
      </span>
    </>
  );
  const bodyStyle: CSSProperties = {
    flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10,
    padding: '0 8px', height: '100%', background: 'none', border: 'none',
    cursor: 'pointer', fontFamily: 'inherit', color: 'inherit',
  };

  return (
    <div
      // Dragged by its whole row, not a handle: at 40px a handle would be the
      // smallest target in the panel, and the row has no competing horizontal
      // gesture.
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', id);
        setDragId(id);
      }}
      onDragEnd={() => setDragId(null)}
      className="wh-row"
      style={{
        display: 'flex', alignItems: 'center', gap: 8, minHeight: 40,
        padding: `0 10px 0 ${indent ? 32 : 16}px`,
        background: selected ? COLORS.primaryContainer : checked ? COLORS.surfaceContainerHigh : undefined,
        opacity: dragId === id ? 0.4 : 1,
        borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
      }}
    >
      {picking && (
        <Icon
          name="drag_handle"
          size={18}
          color={COLORS.outline}
          style={{ flexShrink: 0, cursor: 'grab' }}
        />
      )}
      {picking && (
        <input
          type="checkbox"
          checked={checked}
          onChange={() => setPicked((s) => { const n = new Set(s); if (!n.delete(id)) n.add(id); return n; })}
          aria-label={name}
          className="wh-checkbox"
          style={{ flexShrink: 0 }}
        />
      )}
      {picking ? (
        <button
          type="button"
          onClick={() => setPicked((s) => { const n = new Set(s); if (!n.delete(id)) n.add(id); return n; })}
          style={bodyStyle}
        >
          {label}
        </button>
      ) : onSelect ? (
        <button type="button" onClick={() => onSelect(id)} aria-pressed={selected} style={bodyStyle}>
          {label}
        </button>
      ) : onFocus && point && !dimmed ? (
        <button type="button" onClick={() => onFocus(point)} title={t('pinFolders.centreOn')} style={bodyStyle}>
          {label}
          <Icon name="target" size={18} color={COLORS.outline} />
        </button>
      ) : (
        <div style={{ ...bodyStyle, cursor: 'default' }}>{label}</div>
      )}

      {!picking && onSelect && onFocus && point && !dimmed && (
        <button
          type="button"
          onClick={() => onFocus(point)}
          title={t('pinFolders.centreOn')}
          aria-label={t('pinFolders.centreOnItem', { name })}
          className="wh-btn-icon"
          style={{ flexShrink: 0 }}
        >
          <Icon name="target" size={18} />
        </button>
      )}
      {/* No per-pin visibility control: the design gives the eye to folder
          rows only, and hiding ONE pin lives in its menu. Three trailing
          controls on a 320px row leave nothing for the name. */}
      {!picking && menuItems.length > 0 && (
        <RowMenuButton
          open={menu === `pin:${id}`}
          label={t('pinFolders.pinMenuAria', { name })}
          items={menuItems}
          onToggle={() => setMenu((m) => (m === `pin:${id}` ? null : `pin:${id}`))}
          onClose={() => setMenu(() => null)}
        />
      )}
    </div>
  );
}

/**
 * The show/hide control on a row.
 *
 * Never disabled, and never dimmed. A hidden thing's eye is the ONE control
 * that has to stay reachable — it is the only way back — and greying it out
 * says the opposite: that the row is inert because you hid it. The crossed-out
 * glyph carries the state on its own; the button reads as live in both.
 *
 * (This is where web parts company with the app, which disables a pin's switch
 * while the whole collection is hidden. There, that leaves the master switch as
 * the only way out; here you can set a pin's own state whenever you like and it
 * takes effect as soon as the collection is shown again.)
 */
function VisibilityButton({
  on, label, onToggle,
}: { on: boolean; label: string; onToggle: (next: boolean) => void }) {
  return (
    <button
      type="button"
      className="wh-btn-icon"
      aria-pressed={on}
      aria-label={label}
      title={label}
      onClick={() => onToggle(!on)}
      style={{ flexShrink: 0 }}
    >
      <Icon name={on ? 'visibility' : 'visibility_off'} size={18} color={COLORS.outline} />
    </button>
  );
}

function RowMenuButton({
  open, label, items, onToggle, onClose,
}: { open: boolean; label: string; items: MenuItem[]; onToggle: () => void; onClose: () => void }) {
  const anchor = useRef<HTMLButtonElement>(null);
  return (
    <div className={open ? undefined : 'wh-row-hover-action'} style={{ flexShrink: 0 }}>
      <button
        ref={anchor}
        type="button"
        className="wh-btn-icon"
        onClick={onToggle}
        aria-label={label}
        aria-expanded={open}
        title={label}
      >
        <Icon name="more_vert" size={18} />
      </button>
      {open && <PinMenu items={items} onClose={onClose} anchorRef={anchor} />}
    </div>
  );
}

function SectionHeading({
  text, count, dropActive, onDragOver, onDragLeave, onDrop, visibilitySlot,
}: {
  text: string;
  count: number;
  dropActive?: boolean;
  onDragOver?: () => void;
  onDragLeave?: () => void;
  onDrop?: () => void;
  visibilitySlot?: ReactNode;
}) {
  return (
    <div
      onDragOver={onDragOver ? (e) => { e.preventDefault(); onDragOver(); } : undefined}
      onDragLeave={onDragLeave}
      onDrop={onDrop ? (e) => { e.preventDefault(); onDrop(); } : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, height: 32, padding: '0 16px',
        fontSize: 11, fontWeight: 700, color: COLORS.onSurfaceVariant,
        background: dropActive ? COLORS.primaryContainer : COLORS.surfaceContainerLow,
        outline: dropActive ? `2px dashed ${COLORS.primary}` : undefined,
        outlineOffset: -2,
        textTransform: 'uppercase', letterSpacing: 0.8,
        borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
        borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
        position: 'sticky', top: 0, zIndex: 2,
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>{text}</span>
      <span style={{ fontSize: 12, fontWeight: 600 }}>{count}</span>
      {visibilitySlot}
    </div>
  );
}

/**
 * The label in front of one collection's rows — the team's pins, or the
 * viewer's own.
 *
 * Deliberately heavier and NOT sticky, unlike {@link SectionHeading}: two
 * sticky bands would compete for the same top edge, and a collection is one
 * contiguous block rather than a repeating group. It renders only when the
 * viewer has pins of their own, so a ground with none looks exactly as it did
 * before the two collections were told apart.
 */
function CollectionHeading({
  icon, text, count, visibilitySlot,
}: {
  icon: IconName;
  text: string;
  count: number;
  visibilitySlot?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 8, minHeight: 40, padding: '0 16px',
        fontSize: 13, fontWeight: 600, color: COLORS.onSurface,
        background: COLORS.surfaceContainer,
        borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
        borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
      }}
    >
      <Icon name={icon} size={16} color={COLORS.onSurfaceVariant} />
      <span style={{ flex: 1, minWidth: 0 }}>{text}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.onSurfaceVariant }}>{count}</span>
      {visibilitySlot}
    </div>
  );
}

function EmptyNote({ text, indent }: { text: string; indent?: boolean }) {
  return (
    <div
      style={{
        padding: `14px 16px 14px ${indent ? 44 : 16}px`,
        fontSize: 12, color: COLORS.outline, lineHeight: 1.5,
        borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
      }}
    >
      {text}
    </div>
  );
}

/** Shown when the ground has no pins at all. Import sits next to Add because
 *  most grounds arrive as a GPX file. */
function EmptyState({ onAddPin, onImportGpx }: { onAddPin?: () => void; onImportGpx?: () => void }) {
  const { t } = useTranslation('map');
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
        padding: '46px 28px 30px', textAlign: 'center',
      }}
    >
      <Icon name="folder" size={34} color={COLORS.outlineVariant} />
      <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.onSurface }}>{t('pinFolders.emptyTitle')}</div>
      <div style={{ fontSize: 13, color: COLORS.outline, lineHeight: 1.5 }}>{t('pinFolders.emptyBody')}</div>
      {(onAddPin || onImportGpx) && (
        <div style={{ display: 'flex', gap: 8, paddingTop: 6 }}>
          {onAddPin && (
            <button type="button" className="wh-btn wh-btn-primary" onClick={onAddPin}>
              {t('pinFolders.addPin')}
            </button>
          )}
          {onImportGpx && (
            <button type="button" className="wh-btn wh-btn-outline" onClick={onImportGpx}>
              {t('pinFolders.importGpx')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Skeleton rows hold the panel's height so the map beside it does not jump. */
function SkeletonRows() {
  return (
    <div aria-hidden="true" style={{ padding: '4px 0' }}>
      {Array.from({ length: 8 }, (_, i) => (
        <div
          key={i}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, height: 40,
            padding: '0 16px', borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
          }}
        >
          <span style={{ width: 24, height: 24, borderRadius: 6, background: COLORS.surfaceContainerHigh, flexShrink: 0 }} />
          <span
            style={{
              height: 10, borderRadius: 999, background: COLORS.surfaceContainerHigh,
              // Varied so it reads as content loading rather than as a grid.
              width: `${[62, 48, 71, 55, 66, 43, 58, 50][i]}%`,
            }}
          />
        </div>
      ))}
    </div>
  );
}
